import * as React from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";
import { useContext, useEffect, useState } from "react";
import style from "./UserDenseTable.module.css";
import DialogQualityCheck from "../dialog/DialogQualityCheck";
import Snackbar from "@mui/material/Snackbar";
import { Alert, Button, Menu, MenuItem, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AuthContext, useAuth } from "../../providers/AuthProvider";
import noData from "../../assets/svg/undraw_no-data.svg";

interface User {
  uid: string;
  active: boolean;
  email: string;
  grants: string[];
  displayName: string;
  phoneNumber: string;
}

function createData(
  uid: string,
  active: boolean,
  email: string,
  grants: string[],
  displayName: string,
  phoneNumber: string,
): User {
  return {
    uid: "",
    active: false,
    email: "",
    grants: [],
    displayName: "",
    phoneNumber: "",
  };
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string | Date },
  b: { [key in Key]: number | string | Date },
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof User | "actions";
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "displayName",
    numeric: false,
    disablePadding: false,
    label: "Username",
  },
  {
    id: "active",
    numeric: false,
    disablePadding: false,
    label: "Stato utente",
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "Azioni",
  },
];

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof User,
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler =
    (property: keyof User) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            style={{ fontWeight: "600" }}
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id as keyof User)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
}

interface TableProps {
  data: User[];
  onSelect: (rule: User) => void;
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
  const { numSelected } = props;
  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        },
        numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity,
            ),
        },
      ]}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        ></Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

export default function EnhancedUserTable({ data, onSelect }: TableProps) {
  const [originalRows, setOriginalRows] = useState(data);
  const [snackbarData, setSnackbarData] = React.useState<{
    severity: "warning" | "success";
    message: string;
  }>({ severity: "warning", message: "Non puoi modificare questa regola" });
  const [snackState, setSnackState] = React.useState<boolean>(false);
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof User>("displayName");
  const [selectedUid, setSelectedUid] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [selectedRule, setSelectedItem] = React.useState<any>({});
  const [dialogState, setDialogState] = React.useState<boolean>(false);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [rows, setRows] = useState(data);
  const [canHandle, setCanHandle] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { grants } = useContext(AuthContext);

  const nameFormSchema = z.object({
    displayName: z.string(),
  });
  type FormData = z.infer<typeof nameFormSchema>;
  const form = useForm<FormData>({
    resolver: zodResolver(nameFormSchema),
    defaultValues: {
      displayName: "",
    },
  });
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    setRows(data);
    setOriginalRows(data);
    const canHandle = grants.some((role) =>
      ["quality_op", "admin"].includes(role),
    );
    setCanHandle(canHandle);
  }, [data]);

  const handleClickButtonAction = (
    event: React.MouseEvent<HTMLButtonElement>,
    uid: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedUid(uid);
  };

  const handleCloseButtonAction = (event: "edit" | "", uid?: string) => {
    setAnchorEl(null);
    const idToUse = uid ?? selectedUid;
    if (idToUse) {
      const selectedItem = data.find((item) => item.uid === idToUse);
      if (selectedItem) {
        setSelectedItem(selectedItem);
        onSelect(selectedItem);
      }
    }
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof User,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.uid);
      setSelected(newSelected as any);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy) as any)
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows],
  );

  const filterBy = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = form.getValues("displayName").toLowerCase().replace(/\s+/g, "");
    const filtered = data.filter((item) =>
      item.displayName.toLowerCase().replace(/\s+/g, "").includes(value),
    );
    setRows(filtered);
    setPage(0); // resetta la paginazione
  };

  return (
    <Box sx={{ width: "100%" }}>
      {dialogState && <DialogQualityCheck value={selectedRule} />}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackState}
        onClose={() => setSnackState(false)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => setSnackState(false)}
          severity={snackbarData.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarData.message}
        </Alert>
      </Snackbar>
      <div>
        <form
          onSubmit={(event) => filterBy(event)}
          style={{
            width: "fit-content",
            minWidth: "275px",
            marginBottom: ".5em",
          }}
          className="flex-row gap-2"
        >
          <Controller
            name="displayName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Filtra per nome"
                placeholder="Inserisci nome"
                value={field.value ?? ""}
                error={!!errors.displayName}
                helperText={errors.displayName?.message}
              />
            )}
          />
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            Cerca
          </Button>
          <Button
            onClick={() => {
              setRows(originalRows);
              form.reset();
            }}
          >
            Reset
          </Button>
        </form>
      </div>
      <Paper sx={{ width: "100%", mb: 2 }}>
        {/*<EnhancedTableToolbar numSelected={selected.length} />*/}
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = selected.includes(row.uid as string);
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={row.uid}
                    selected={isItemSelected}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell
                      component="th"
                      align="left"
                      id={labelId}
                      scope="row"
                      padding="normal"
                    >
                      {row.displayName.toUpperCase()}
                    </TableCell>
                    <TableCell padding="normal" align="left">
                      <span className={row.active ? `active` : `suspend`}>
                        {row.active ? "Attivo" : "Sospeso"}
                      </span>
                    </TableCell>
                    <TableCell padding="normal" align="left">
                      <Button
                        onClick={(event) =>
                          handleClickButtonAction(event, row.uid as string)
                        }
                      >
                        Azioni
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  key="empty-rows"
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
              {visibleRows.length === 0 && (
                <TableRow key={1}>
                  <TableCell colSpan={6} align="center">
                    <div
                      style={{ alignItems: "center" }}
                      className={"flex-col"}
                    >
                      <img
                        style={{ width: "125px", paddingBottom: ".5em" }}
                        src={noData}
                        alt="delle cartelline vuote"
                      />
                      <span style={{ fontWeight: "300", fontSize: "14px" }}>
                        Nessun dato
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <Menu
              id="menu-actions"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => handleCloseButtonAction("")}
              MenuListProps={{ "aria-labelledby": "menu-actions" }}
            >
              <MenuItem
                onClick={() => handleCloseButtonAction("edit", selectedUid!)}
              >
                Modifica utente
              </MenuItem>
            </Menu>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      {/*<FormControlLabel*/}
      {/*  control={<Switch checked={dense} onChange={handleChangeDense} />}*/}
      {/*  label="Dense padding"*/}
      {/*/>*/}
    </Box>
  );
}
